import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import instance from "../../../instance/AxiosInstance";
import Style from "./Index.module.css";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LoadingSpin from "react-loading-spin";
import { Helmet } from "react-helmet-async";

const DeleteAccount = () => {
    const navigate = useNavigate();

    // Loading state
    const [loading, setLoading] = useState(false);

    // Toggle between email and phone
    const [toggle, setToggle] = useState(true); // true = email, false = phone

    // Form data
    const [formData, setFormData] = useState({
        email: "",
        phoneNumber: "",
        reason: ""
    });

    // Confirmation state
    const [showConfirmation, setShowConfirmation] = useState(false);

    // Deletion reasons
    const deletionReasons = [
        "No longer need the service",
        "Privacy concerns",
        "Too many notifications",
        "Found a better alternative",
        "Difficult to use",
        "Other"
    ];

    // Handle form submission
    const submitHandler = (e) => {
        e.preventDefault();

        // Validation
        if ((toggle && !formData.email) || (!toggle && !formData.phoneNumber)) {
            toast.error("Please enter your " + (toggle ? "email" : "phone number"));
            return;
        }

        if (!formData.reason) {
            toast.error("Please select a reason for deletion");
            return;
        }

        // Show confirmation dialog
        setShowConfirmation(true);
    };

    // Handle permanent deletion
    const handlePermanentDelete = () => {
        setLoading(true);

        const payload = {
            reason: formData.reason
        };

        if (toggle) {
            payload.email = formData.email;
        } else {
            payload.phoneNumber = formData.phoneNumber;
        }

        instance.post("api/delete-account", payload)
            .then((response) => {
                setLoading(false);
                toast.success("Your account has been permanently deleted");

                // Clear local storage
                localStorage.clear();

                // Redirect to home page after 2 seconds
                setTimeout(() => {
                    navigate("/");
                }, 2000);
            })
            .catch((error) => {
                setLoading(false);
                toast.error(error.response?.data?.error || "Failed to delete account");
                setShowConfirmation(false);
            });
    };

    return (
        <>
            <Helmet>
                <title>Delete Account - DealNBuy</title>
                <meta name="description" content="Delete your DealNBuy account permanently" />
            </Helmet>

            <div className={Style.container}>
                {showConfirmation ? (
                    <div className={Style.form_container}>
                        <div className={Style.confirmation_box}>
                            <h1>⚠️ Confirm Account Deletion</h1>
                            <div className={Style.warning_content}>
                                <p>Are you absolutely sure you want to delete your account?</p>
                                <p className={Style.warning_text}>
                                    This action is <strong>PERMANENT</strong> and cannot be undone. All your data will be deleted:
                                </p>
                                <ul className={Style.deletion_list}>
                                    <li>Your profile information</li>
                                    <li>All your posted products</li>
                                    <li>Your messages and conversations</li>
                                    <li>Your wishlist items</li>
                                    <li>Your notifications</li>
                                </ul>
                                <div className={Style.button_group}>
                                    <button
                                        className={Style.cancel_button}
                                        onClick={() => setShowConfirmation(false)}
                                        disabled={loading}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className={Style.delete_button}
                                        onClick={handlePermanentDelete}
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <LoadingSpin size="20px" direction="alternate" width="4px" />
                                        ) : (
                                            "Yes, Delete Permanently"
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className={Style.form_container}>
                        <div className={Style.left}>
                            <h1>Delete Your Account</h1>
                            <p className={Style.subtitle}>
                                We're sorry to see you go. Please provide your account information and reason for deletion.
                            </p>

                            <form onSubmit={submitHandler}>
                                {/* Email or Phone Toggle */}
                                {toggle ? (
                                    <div className={Style.input_group}>
                                        <label htmlFor="deleteEmail">Email Address</label>
                                        <input
                                            type="email"
                                            placeholder="Enter your registered email"
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            id="deleteEmail"
                                            value={formData.email}
                                        />
                                    </div>
                                ) : (
                                    <div className={Style.input_group}>
                                        <label htmlFor="deletePhone">Phone Number</label>
                                        <input
                                            type="tel"
                                            placeholder="Enter your registered phone number"
                                            id="deletePhone"
                                            value={formData.phoneNumber}
                                            onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                                        />
                                    </div>
                                )}

                                {/* Reason for deletion */}
                                <div className={Style.input_group}>
                                    <label htmlFor="deleteReason">Reason for Deletion</label>
                                    <select
                                        id="deleteReason"
                                        value={formData.reason}
                                        onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                                    >
                                        <option value="">Select a reason</option>
                                        {deletionReasons.map((reason, index) => (
                                            <option key={index} value={reason}>{reason}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Additional feedback for "Other" */}
                                {formData.reason === "Other" && (
                                    <div className={Style.input_group}>
                                        <label htmlFor="otherReason">Please specify</label>
                                        <textarea
                                            id="otherReason"
                                            placeholder="Tell us more about your reason..."
                                            rows="3"
                                            onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                                        />
                                    </div>
                                )}

                                <button type="submit" className={Style.submit_button}>
                                    Continue to Delete
                                </button>

                                <p className={Style.toggle_text}>
                                    {toggle ? (
                                        <span onClick={() => setToggle(false)}>
                                            Use phone number instead
                                        </span>
                                    ) : (
                                        <span onClick={() => setToggle(true)}>
                                            Use email instead
                                        </span>
                                    )}
                                </p>
                            </form>
                        </div>

                        <div className={Style.right}>
                            <img src="/Images/forgot.svg" alt="Delete Account" />
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default DeleteAccount;
